import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './components/theme-provider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import SecurityWarningModal from './components/SecurityWarningModal';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Layout from './pages/Layout';

// Lazy load components with better chunking
const Dashboard = lazy(() => 
  import('./pages/Dashboard')
);
const Operations = lazy(() => 
  import('./pages/Operations')
);
const TraderDiary = lazy(() => 
  import('./pages/TraderDiary')
);
const StratonLogin = lazy(() => 
  import('./pages/StratonLogin')
);
const StratonArea = lazy(() => 
  import('./pages/StratonArea')
);
const PaintStraton = lazy(() => 
  import('./pages/PaintStraton')
);
const News = lazy(() => 
  import('./pages/News')
);
const Support = lazy(() => 
  import('./pages/Support')
);
const EliteMembers = lazy(() =>
  import('./pages/EliteMembers')
);
const StratonTests = lazy(() =>
  import('./pages/StratonTests')
);
const FimaTroN = lazy(() =>
  import('./pages/FimaTroN')
);
const StratonBacktest = lazy(() =>
  import('./pages/StratonBacktest')
);

// Optimized loading fallback
const LoadingFallback = React.memo(() => {
  return (
    <div className="flex items-center justify-center p-8">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#bf953f]"></div>
    </div>
  );
});

LoadingFallback.displayName = 'LoadingFallback';

// Optimized NotFound component
const NotFound = React.memo(() => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c] mb-4">
          404 - Página não encontrada
        </h1>
        <p className="text-muted-foreground mb-8">
          A página que você está procurando não existe.
        </p>
        <a 
          href="/dashboard" 
          className="inline-flex items-center px-4 py-2 text-white bg-gradient-to-r from-[#bf953f] via-[#b38728] to-[#aa771c] hover:from-[#aa771c] hover:via-[#b38728] hover:to-[#bf953f] rounded-lg transition-all duration-300"
        >
          Voltar ao Dashboard
        </a>
      </div>
    </div>
  );
});

NotFound.displayName = 'NotFound';

// Optimized AppContent with memoization
const AppContent = React.memo(() => {
  const { showSecurityWarning, setShowSecurityWarning } = useAuth();

  return (
    <>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/FimaTroN" element={
            <Suspense fallback={<LoadingFallback />}>
              <FimaTroN />
            </Suspense>
          } />
          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={
              <Suspense fallback={<LoadingFallback />}>
                <Dashboard />
              </Suspense>
            } />
            <Route path="operations" element={
              <Suspense fallback={<LoadingFallback />}>
                <Operations />
              </Suspense>
            } />
            <Route path="diary" element={
              <Suspense fallback={<LoadingFallback />}>
                <TraderDiary />
              </Suspense>
            } />
            <Route path="straton-login" element={
              <Suspense fallback={<LoadingFallback />}>
                <StratonLogin />
              </Suspense>
            } />
            <Route path="straton-area" element={
              <Suspense fallback={<LoadingFallback />}>
                <StratonArea />
              </Suspense>
            } />
            <Route path="straton-tests" element={
              <Suspense fallback={<LoadingFallback />}>
                <StratonTests />
              </Suspense>
            } />
            <Route path="backtest" element={
              <Suspense fallback={<LoadingFallback />}>
                <StratonBacktest />
              </Suspense>
            } />
            <Route path="paint" element={
              <Suspense fallback={<LoadingFallback />}>
                <PaintStraton />
              </Suspense>
            } />
            <Route path="news" element={
              <Suspense fallback={<LoadingFallback />}>
                <News />
              </Suspense>
            } />
            <Route path="support" element={
              <Suspense fallback={<LoadingFallback />}>
                <Support />
              </Suspense>
            } />
            <Route path="elite" element={
              <Suspense fallback={<LoadingFallback />}>
                <EliteMembers />
              </Suspense>
            } />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>

      {/* Modal de Aviso de Segurança */}
      <SecurityWarningModal
        isOpen={showSecurityWarning}
        onClose={() => setShowSecurityWarning(false)}
      />

      <Toaster
        position="top-right"
        toastOptions={{
          className: 'bg-background border border-border',
          style: {
            background: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
            border: '1px solid hsl(var(--border))',
          },
        }}
      />
    </>
  );
});

AppContent.displayName = 'AppContent';

function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="straton-theme">
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;